/**
 * Tech-aware scan routing between the DTG and DTF print stations
 * (Phase 50.01 / BLU-233).
 *
 * The operator decision: keep the two print stations separate, but make a
 * garment scan route the operator to the CORRECT station for its print
 * technology. The print-technology axis rides the garment QR's `m` field
 * (`'DTG' | 'DTF'`), made real for DTF in plan 50.01.3 — decode via the
 * canonical `decodeGarmentQR` so we never hand-roll QR parsing here.
 *
 * Single source of truth for the tech → station-route mapping. Both
 * `PrintStationPage` (DTG) and `FilmPrintPage` (DTF) consume this so the
 * mapping is never duplicated.
 */
import { decodeGarmentQR } from './qr-encoder';

/** The two print technologies we route between. */
export type PrintTechnology = 'DTG' | 'DTF';

/**
 * Canonical route for each print technology's station. This is the ONE place
 * the tech → route mapping lives; do not inline these strings in the stations.
 *
 * - DTG → `/production/printer`     (PrintStationPage)
 * - DTF → `/production/film-printing` (FilmPrintPage)
 */
export const PRINT_TECH_ROUTES = {
  DTG: '/production/printer',
  DTF: '/production/film-printing',
} as const satisfies Record<PrintTechnology, string>;

/**
 * Query-param key used to carry the scanned garment id to the target station
 * so it can focus / pre-select the garment after a cross-tech redirect.
 */
export const SCAN_ROUTE_GARMENT_PARAM = 'garment';

/**
 * UUID v4-ish shape. `extractGarmentId` returns any non-empty string from the
 * decoded payload's `g` field, which then flows into the `?garment=` nav param.
 * Defense-in-depth: only carry a well-formed garment id into the URL — a
 * malformed value is dropped so a tampered/garbage QR can't smuggle arbitrary
 * query content downstream.
 */
const GARMENT_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isValidGarmentId(garmentId: string | null | undefined): garmentId is string {
  return typeof garmentId === 'string' && GARMENT_ID_RE.test(garmentId);
}

/**
 * Map a print technology to its station route.
 *
 * @example
 * printTechnologyRoute('DTG'); // '/production/printer'
 * printTechnologyRoute('DTF'); // '/production/film-printing'
 */
export function printTechnologyRoute(tech: PrintTechnology): string {
  return PRINT_TECH_ROUTES[tech];
}

/** The technology a DTF/DTG switch goes to. */
export function otherPrintTechnology(tech: PrintTechnology): PrintTechnology {
  return tech === 'DTF' ? 'DTG' : 'DTF';
}

/**
 * Read the print-technology hint (`m`) off a scanned garment QR.
 *
 * Returns `'DTG' | 'DTF'` for a valid V1/V2 garment label, or `null` when the
 * string is not a decodable garment QR (bin/shelf/garbage). V1 labels decode to
 * `m:'DTG'` — correct, because DTF did not exist pre-V2.
 */
export function readScanPrintTechnology(qrCode: string): PrintTechnology | null {
  const payload = decodeGarmentQR(qrCode);
  if (!payload) return null;
  // `m` is validated by the encoder's Zod schema to be 'DTG' | 'DTF'.
  return payload.m;
}

/**
 * Build the absolute target path (with the garment context query param) for a
 * cross-tech redirect.
 *
 * @example
 * buildTechRoutePath('DTF', 'gid-123');
 * // '/production/film-printing?garment=gid-123'
 */
export function buildTechRoutePath(tech: PrintTechnology, garmentId?: string | null): string {
  const base = printTechnologyRoute(tech);
  // Only carry a well-formed garment id; drop to the base path otherwise.
  if (!isValidGarmentId(garmentId)) return base;
  const params = new URLSearchParams({ [SCAN_ROUTE_GARMENT_PARAM]: garmentId });
  return `${base}?${params.toString()}`;
}

/**
 * Result of evaluating a scan against the station the operator is currently on.
 *
 * - `crossTech: false` → the scan belongs on this station (or isn't a garment
 *   QR at all); the caller proceeds with its normal scan handling.
 * - `crossTech: true`  → the scanned garment's tech differs from this station;
 *   the caller should give cross-tech feedback + navigate to `targetRoute`.
 */
export type ScanRouteEvaluation =
  | { crossTech: false; tech: PrintTechnology | null }
  | {
      crossTech: true;
      tech: PrintTechnology;
      targetRoute: string;
      targetPath: string;
      garmentId: string | null;
    };

/**
 * Decide whether a scanned QR should redirect off the current station.
 *
 * Pure + side-effect-free so it is trivially unit-testable. The station passes
 * its own technology (`stationTech`) and the raw scan; we decode the tech and
 * compare.
 *
 * A non-garment QR (no decodable `m`) returns `{ crossTech: false, tech: null }`
 * so the station's existing scan handler owns the error UX (wrong-type / bin /
 * unknown) — we do NOT swallow it here.
 */
export function evaluateScanRoute(
  stationTech: PrintTechnology,
  qrCode: string,
  garmentId?: string | null,
): ScanRouteEvaluation {
  const tech = readScanPrintTechnology(qrCode);
  if (!tech || tech === stationTech) {
    return { crossTech: false, tech };
  }
  // Normalize the garment id once so the returned `garmentId` and `targetPath`
  // agree — a malformed id is dropped from both (buildTechRoutePath drops it
  // from the URL; we null it here so the caller never focuses on garbage).
  const safeGarmentId = isValidGarmentId(garmentId) ? garmentId : null;
  return {
    crossTech: true,
    tech,
    targetRoute: printTechnologyRoute(tech),
    targetPath: buildTechRoutePath(tech, safeGarmentId),
    garmentId: safeGarmentId,
  };
}
